/**
 * The records the local store holds (decision 28: one record per chapter; decision 51: plaintext) and their
 * readers. Every record read from IndexedDB is checked here before it crosses into typed code (Definition of
 * Done, security): another build or a damaged database can leave anything in a store. Part 06 log, table R.
 */
import { KEPT_SIDE } from '@cockatrice/core';
import type { ImageFormat } from '@cockatrice/core';

/** A stored record that isn't what its store holds. `field` names the offending field, or null for the whole. */
export class StoreRecordError extends Error {
  override readonly name = 'StoreRecordError';
  readonly field: string | null;

  constructor(field: string | null, problem: string) {
    super(field === null ? problem : `${field}: ${problem}`);
    this.field = field;
  }
}

/**
 * Part 24d, S′11: a write that hands a staged import to its book (`adopt-import`) found no pending import of it: it was
 * abandoned meanwhile, so nothing of the write was applied.
 */
export class ImportGoneError extends Error {
  override readonly name = 'ImportGoneError';

  constructor(bookId: string) {
    super(`no pending import of ${bookId.slice(0, 64)}`);
  }
}

/**
 * Part 24c, principle 2: a write that creates a record (`add`, `add-image`) met a key a record already holds; nothing of
 * the write was applied.
 */
export class KeyTakenError extends Error {
  override readonly name = 'KeyTakenError';
  /** The store whose key was taken. */
  readonly store: string;

  constructor(store: string) {
    super(`a record of ${store} already holds the key`);
    this.store = store;
  }
}

/** The text of one chapter, as the editor saves it (decision 28). */
export interface ChapterText {
  /** The chapter's ID (R3). */
  readonly id: string;
  /** The book the chapter belongs to (R3). */
  readonly bookId: string;
  /** The stored source (decision 66). */
  readonly text: string;
}

/**
 * Why a conflict copy was made: a stale tab's typing (part 06, sync case table L1), the text a chapter had when the
 * writer took a re-imported Word file over it (part 23b, L9), or when a bundle's update merged into it (part 24d, L11).
 */
export type ConflictOrigin = 'stale-tab' | 'docx-reimport' | 'bundle-update';

/** Every conflict copy origin. */
export const CONFLICT_ORIGINS: readonly ConflictOrigin[] = Object.freeze(['stale-tab', 'docx-reimport', 'bundle-update']);

/**
 * A local conflict copy: typing a tab couldn't save as the chapter because it lost the edit lock (sync case
 * table, L1), or a chapter's text before a re-import replaced it (L9). It has a fresh ID, so creating it overwrites
 * nothing; the active tab pushes it (part 32).
 */
export interface ConflictCopy {
  /** The copy's own ID, fresh (R3). */
  readonly id: string;
  /** The chapter it is a copy of (R3). */
  readonly chapterId: string;
  /** The chapter's book (R3). */
  readonly bookId: string;
  /** The text it keeps: the typing, or the chapter as it was before a re-import. */
  readonly text: string;
  /** When it was made, in milliseconds since the Unix epoch. */
  readonly createdAt: number;
  /** Why it was made. */
  readonly origin: ConflictOrigin;
}

/**
 * Why a snapshot was taken (part 07 log, table S). Every reason but `interval` is an event (rule K4). `from-bundle` (part
 * 24d, table B′) holds a bundle's text of a chapter its update didn't write whole.
 */
export type SnapshotReason = 'interval' | 'before-conflict' | 'before-restore' | 'before-replace-all' | 'before-remote' | 'before-reimport' | 'before-update' | 'from-bundle';

/** Every snapshot reason, in table S's order. */
export const SNAPSHOT_REASONS: readonly SnapshotReason[] = Object.freeze([
  'interval',
  'before-conflict',
  'before-restore',
  'before-replace-all',
  'before-remote',
  'before-reimport',
  'before-update',
  'from-bundle',
]);

/**
 * What keeps a snapshot out of thinning (decision 29; part 07 log, rule P): a share link showing it (until the link
 * is revoked or expires, part 38), or a DOCX export based on it (until `until`, the export time + 12 months, decision 64).
 * Fix-next 27, table PT: a DOCX export's pin keeps the chapter's title as exported, when every export it stands for
 * exported the same one (none on a pin written before, or after exports of two titles: the base title is unknown).
 */
export type SnapshotPin =
  | { readonly kind: 'share-link'; readonly linkId: string }
  | { readonly kind: 'docx-export'; readonly until: number; readonly title?: string }
  | { readonly kind: 'bundle'; readonly until: number };

/**
 * A snapshot of a chapter, without its text (decision 29; part 07 log). The text is a {@link SnapshotText} under the
 * same ID, so thinning reads only these small records.
 */
export interface Snapshot {
  readonly id: string;
  readonly bookId: string;
  readonly chapterId: string;
  /** Whose history it is: the device that made it, or an import (decision 29). */
  readonly owner: string;
  /** Its place in its owner's history of the chapter: creation order for own snapshots, receipt order otherwise. */
  readonly seq: number;
  /** When it was made, clamped on entry (principle 4), in milliseconds since the Unix epoch. */
  readonly createdAt: number;
  /**
   * When a thinning first found it stamped before a snapshot made earlier (a clock that was behind, or one that ran
   * ahead before it): from then on K1 and K4 age it from this time instead of its stamp; null until then (rule S).
   */
  readonly settled: number | null;
  /** The time zone offset it was made in, in minutes east of UTC (principle 3). */
  readonly zone: number;
  readonly reason: SnapshotReason;
  readonly pins: readonly SnapshotPin[];
}

/** A snapshot's text, under the snapshot's ID. */
export interface SnapshotText {
  readonly id: string;
  readonly text: string;
}

/** The time zone offsets there are: UTC−12:00 to UTC+14:00, in minutes. */
export const MIN_ZONE = -720;
export const MAX_ZONE = 840;

/** The largest epoch a store holds (W12): a bump past it would lose integer precision. */
export const MAX_EPOCH = Number.MAX_SAFE_INTEGER;

/** Whether `value` is an ID: 32 lowercase hex digits, the random 128-bit form of decision 37 (R3). */
export function isId(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{32}$/u.test(value);
}

const isSafeInteger = (value: unknown): value is number => Number.isSafeInteger(value);

/** Whether `value` is a whole number from 0 to {@link MAX_EPOCH}. */
function isCount(value: unknown): value is number {
  return isSafeInteger(value) && value >= 0;
}

/** A record's fields, read once each, with errors named from `path` (a nested object's place, or null for a record). */
class Fields {
  private readonly values: ReadonlyMap<string, unknown>;
  private readonly path: string | null;

  constructor(values: ReadonlyMap<string, unknown>, path: string | null) {
    this.values = values;
    this.path = path;
  }

  /** The error name of `key`. */
  name(key: string): string {
    return this.path === null ? key : `${this.path}.${key}`;
  }

  get(key: string): unknown {
    return this.values.get(key);
  }

  /** A SHA-256 as 64 lowercase hex digits. */
  hash(key: string): string {
    const value = this.values.get(key);
    if (typeof value !== 'string' || !/^[0-9a-f]{64}$/u.test(value)) throw new StoreRecordError(this.name(key), 'must be 64 lowercase hex digits');
    return value;
  }

  id(key: string): string {
    const value = this.values.get(key);
    if (!isId(value)) throw new StoreRecordError(this.name(key), 'must be 32 lowercase hex digits');
    return value;
  }

  string(key: string): string {
    const value = this.values.get(key);
    if (typeof value !== 'string') throw new StoreRecordError(this.name(key), 'must be a string');
    return value;
  }

  /** A whole number from 0 (-0 reads as 0); `unit` names it in the error. */
  count(key: string, unit: string): number {
    const value = this.values.get(key);
    if (!isCount(value)) throw new StoreRecordError(this.name(key), `must be a whole number of ${unit}`);
    return value + 0;
  }
}

/**
 * The own data properties of a plain object with exactly `keys` (R2), each read once; errors name `path` (null for a
 * whole record).
 */
function readFields(value: unknown, keys: readonly string[], what: string, path: string | null = null): Fields {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new StoreRecordError(path, `${what} must be an object`);
  }
  const prototype: unknown = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) throw new StoreRecordError(path, `${what} must be a plain object`);
  const fields = new Map<string, unknown>();
  const named = new Fields(fields, path);
  for (const own of Reflect.ownKeys(value)) {
    const key = keys.find((name) => name === own);
    if (key === undefined) throw new StoreRecordError(path, `${what} has an unknown field "${String(own).slice(0, 64)}"`);
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor === undefined || !('value' in descriptor)) throw new StoreRecordError(named.name(key), 'must be a data property');
    fields.set(key, descriptor.value);
  }
  for (const key of keys) if (!fields.has(key)) throw new StoreRecordError(named.name(key), 'is missing');
  return named;
}

const CHAPTER_KEYS = ['id', 'bookId', 'text'] as const;

/** Reads a chapter record (R1–R3); anything else throws {@link StoreRecordError}. */
export function readChapterText(value: unknown): ChapterText {
  const fields = readFields(value, CHAPTER_KEYS, 'a chapter');
  return { id: fields.id('id'), bookId: fields.id('bookId'), text: fields.string('text') };
}

const COPY_KEYS = ['id', 'chapterId', 'bookId', 'text', 'createdAt', 'origin'] as const;

/** Reads a conflict-copy record (R1–R3); anything else throws {@link StoreRecordError}. */
export function readConflictCopy(value: unknown): ConflictCopy {
  const fields = readFields(value, COPY_KEYS, 'a conflict copy');
  const createdAt = fields.count('createdAt', 'milliseconds');
  const origin = CONFLICT_ORIGINS.find((known) => known === fields.get('origin'));
  if (origin === undefined) throw new StoreRecordError('origin', `must be one of ${CONFLICT_ORIGINS.join(', ')}`);
  return {
    id: fields.id('id'),
    chapterId: fields.id('chapterId'),
    bookId: fields.id('bookId'),
    text: fields.string('text'),
    createdAt,
    origin,
  };
}

/** Reads one pin of a snapshot (R2), naming `pins[index]` in errors. */
function readPin(value: unknown, index: number): SnapshotPin {
  const path = `pins[${String(index)}]`;
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'share-link') return { kind, linkId: readFields(value, ['kind', 'linkId'], 'a pin', path).id('linkId') };
  if (kind === 'docx-export' && typeof value === 'object' && value !== null && Object.hasOwn(value, 'title')) {
    const fields = readFields(value, ['kind', 'until', 'title'], 'a pin', path);
    return { kind, until: fields.count('until', 'milliseconds'), title: fields.string('title') };
  }
  if (kind === 'docx-export' || kind === 'bundle') return { kind, until: readFields(value, ['kind', 'until'], 'a pin', path).count('until', 'milliseconds') };
  throw new StoreRecordError(path, 'must be a share-link, docx-export or bundle pin');
}

const SNAPSHOT_KEYS = ['id', 'bookId', 'chapterId', 'owner', 'seq', 'createdAt', 'settled', 'zone', 'reason', 'pins'] as const;

/** Reads a snapshot record (R1–R3, part 07); anything else throws {@link StoreRecordError}. */
export function readSnapshot(value: unknown): Snapshot {
  const fields = readFields(value, SNAPSHOT_KEYS, 'a snapshot');
  const zone = fields.get('zone');
  if (!isSafeInteger(zone) || zone < MIN_ZONE || zone > MAX_ZONE) throw new StoreRecordError('zone', `must be a whole number of minutes from ${String(MIN_ZONE)} to ${String(MAX_ZONE)}`);
  const reason = SNAPSHOT_REASONS.find((known) => known === fields.get('reason'));
  if (reason === undefined) throw new StoreRecordError('reason', `must be one of ${SNAPSHOT_REASONS.join(', ')}`);
  const pins = fields.get('pins');
  if (!Array.isArray(pins)) throw new StoreRecordError('pins', 'must be a list');
  return {
    id: fields.id('id'),
    bookId: fields.id('bookId'),
    chapterId: fields.id('chapterId'),
    owner: fields.id('owner'),
    seq: fields.count('seq', 'steps'),
    createdAt: fields.count('createdAt', 'milliseconds'),
    settled: fields.get('settled') === null ? null : fields.count('settled', 'milliseconds or null'),
    zone: zone + 0,
    reason,
    pins: pins.map((pin: unknown, index) => readPin(pin, index)),
  };
}

const SNAPSHOT_TEXT_KEYS = ['id', 'text'] as const;

/** Reads a snapshot's text record (R1–R3); anything else throws {@link StoreRecordError}. */
export function readSnapshotText(value: unknown): SnapshotText {
  const fields = readFields(value, SNAPSHOT_TEXT_KEYS, 'a snapshot text');
  return { id: fields.id('id'), text: fields.string('text') };
}

/**
 * Reads the stored lock epoch (W10, W11): a missing record is 0, anything but a whole number from 0 to
 * {@link MAX_EPOCH} throws {@link StoreRecordError}.
 */
export function readEpoch(value: unknown): number {
  if (value === undefined) return 0;
  if (!isCount(value)) throw new StoreRecordError('epoch', `must be a whole number from 0 to ${MAX_EPOCH}`);
  return value + 0;
}

/**
 * When and where a chapter went to the trash (decision 68; the local-trash rule: no timer, "deleted on <device>";
 * part 08 log, B7). The chapter's text, snapshots and conflict copies stay under its ID until the writer deletes it
 * forever (B10).
 */
export interface Trashed {
  /** Milliseconds since the Unix epoch. */
  readonly at: number;
  /** The device that deleted it: this one (B7), or another through a remote tombstone (X9, part 33). */
  readonly device: string;
}

/**
 * A chapter entry (decision 62: one record per chapter entry with its title, position and synopsis; its notes are note
 * records since part 19e, decision 76). Its ID is the chapter's, shared with its {@link ChapterText}. The position is kept
 * whatever it holds: one that isn't valid puts the chapter at the end, never out of sight (decision 62; part 08 log, O3).
 */
export interface ChapterEntry {
  readonly id: string;
  readonly bookId: string;
  readonly title: string;
  /** Its place in the book (`isPosition` in @cockatrice/core); any string is stored. */
  readonly position: string;
  readonly synopsis: string;
  /** Null while the chapter is in the book. */
  readonly trashed: Trashed | null;
}

const ENTRY_KEYS = ['id', 'bookId', 'title', 'position', 'synopsis', 'trashed'] as const;

/** An entry's fields as `fields` holds them. */
function entryOf(fields: Fields): ChapterEntry {
  const trashed = fields.get('trashed');
  return {
    id: fields.id('id'),
    bookId: fields.id('bookId'),
    title: fields.string('title'),
    position: fields.string('position'),
    synopsis: fields.string('synopsis'),
    trashed: trashed === null ? null : readTrashed(trashed),
  };
}

/** Reads a chapter entry record (R1–R3, part 08); anything else throws {@link StoreRecordError}. */
export function readChapterEntry(value: unknown): ChapterEntry {
  return entryOf(readFields(value, ENTRY_KEYS, 'a chapter entry'));
}

/**
 * Part 19e, MG1–MG4: reads a chapter entry as database version 9 and earlier stored it, with the chapter's notes in a
 * field of their own; anything else throws {@link StoreRecordError}.
 */
export function readLegacyEntry(value: unknown): { readonly entry: ChapterEntry; readonly notes: string } {
  const fields = readFields(value, [...ENTRY_KEYS, 'notes'], 'a chapter entry');
  return { entry: entryOf(fields), notes: fields.string('notes') };
}

/** Reads the `trashed` field of an entry (R2). */
function readTrashed(value: unknown): Trashed {
  const fields = readFields(value, ['at', 'device'], 'a deletion', 'trashed');
  return { at: fields.count('at', 'milliseconds'), device: fields.id('device') };
}

/**
 * A book (decisions 13, 62: one record for the book's theme and metadata). Its metadata and theme are part 05's
 * serialised forms, kept as text: the app reads them with part 05's `metadataFromJson` and `themeFromJson`, which
 * refuse anything invalid, so this reader checks only that they are text (part 11).
 */
export interface BookRecord {
  readonly id: string;
  /** Part 05's `serializeMetadata` output. */
  readonly metadata: string;
  /** Part 05's `serializeTheme` output. */
  readonly theme: string;
  /** When the book was made on this device or imported, in milliseconds since the Unix epoch: the book list's order. */
  readonly createdAt: number;
}

const BOOK_KEYS = ['id', 'metadata', 'theme', 'createdAt'] as const;

/** Reads a book record (R1–R3, part 11); anything else throws {@link StoreRecordError}. */
export function readBook(value: unknown): BookRecord {
  const fields = readFields(value, BOOK_KEYS, 'a book');
  return { id: fields.id('id'), metadata: fields.string('metadata'), theme: fields.string('theme'), createdAt: fields.count('createdAt', 'milliseconds') };
}

/**
 * This device (part 11): its ID, which owns its snapshots (decision 29; part 07 log) and names it where it deleted a
 * chapter (part 08, B7), and the app's settings for this device (decision 16: editor comfort), kept as text the app
 * reads with its own checked parser. One record; part 36 replaces the ID with the device signing key's.
 */
export interface DeviceRecord {
  readonly id: string;
  /** The app's serialised device settings. */
  readonly settings: string;
}

const DEVICE_KEYS = ['id', 'settings'] as const;

/** Reads a device record (R1–R3, part 11); anything else throws {@link StoreRecordError}. */
export function readDevice(value: unknown): DeviceRecord {
  const fields = readFields(value, DEVICE_KEYS, 'a device');
  return { id: fields.id('id'), settings: fields.string('settings') };
}

/**
 * One image of a book (decision 37; part 16a log, V1): its random ID, the SHA-256 of its bytes, and what the layout and
 * the cap read without loading them. The bytes are an {@link ImageBytesRecord} under the same book and ID. Both are kept
 * under the book and the ID together, so the same ID in two books is two images (a book imported as a copy, L11).
 */
export interface ImageRecord {
  readonly bookId: string;
  readonly id: string;
  /** The SHA-256 of the bytes, 64 lowercase hex digits, as every reference carries it. */
  readonly sha256: string;
  readonly format: ImageFormat;
  /** In pixels, from 1 to the cover's kept side (2560). */
  readonly width: number;
  readonly height: number;
  /** The bytes' length: what the image counts against the book's cap. */
  readonly size: number;
}

/** An image's bytes (V2): the stored JPEG or PNG, under the image's book and ID. */
export interface ImageBytesRecord {
  readonly bookId: string;
  readonly id: string;
  readonly bytes: Uint8Array;
}

/**
 * The store of image records, indexed by book (`bookId`, the cap) and by book and hash (`bookHash`, the dedupe), and the
 * store of their bytes (part 16a, table U); both keyed by book and ID.
 */
export const IMAGES = 'images';
export const IMAGE_BYTES = 'imageBytes';

const IMAGE_KEYS = ['bookId', 'id', 'sha256', 'format', 'width', 'height', 'size'] as const;
const IMAGE_FORMATS: readonly ImageFormat[] = ['jpeg', 'png'];

/** Reads an image record (V1, V3); anything else throws {@link StoreRecordError}. */
export function readImage(value: unknown): ImageRecord {
  const fields = readFields(value, IMAGE_KEYS, 'an image');
  const format = IMAGE_FORMATS.find((known) => known === fields.get('format'));
  if (format === undefined) throw new StoreRecordError('format', 'must be jpeg or png');
  const side = (key: string): number => {
    const size = fields.count(key, 'pixels');
    if (size < 1 || size > KEPT_SIDE.cover) throw new StoreRecordError(key, `must be from 1 to ${String(KEPT_SIDE.cover)} pixels`);
    return size;
  };
  const size = fields.count('size', 'bytes');
  if (size < 1) throw new StoreRecordError('size', 'must be at least 1 byte');
  return { bookId: fields.id('bookId'), id: fields.id('id'), sha256: fields.hash('sha256'), format, width: side('width'), height: side('height'), size };
}

const IMAGE_BYTES_KEYS = ['bookId', 'id', 'bytes'] as const;

/** Reads an image's bytes record (V2, V3); anything else throws {@link StoreRecordError}. */
export function readImageBytes(value: unknown): ImageBytesRecord {
  const fields = readFields(value, IMAGE_BYTES_KEYS, 'image bytes');
  const bytes = fields.get('bytes');
  if (!(bytes instanceof Uint8Array) || Object.getPrototypeOf(bytes) !== Uint8Array.prototype || bytes.length === 0) {
    throw new StoreRecordError('bytes', 'must be a Uint8Array of at least 1 byte');
  }
  return { bookId: fields.id('bookId'), id: fields.id('id'), bytes };
}

/** What a book note is about (decision 41: character and place notes). */
export type NoteKind = 'character' | 'place';

/** Every note kind, in the order the notes dialog showed them. */
export const NOTE_KINDS: readonly NoteKind[] = Object.freeze(['character', 'place']);

/**
 * A character or place note of a book as database version 9 and earlier stored it (part 19c log, table K) and bundles of
 * versions 3 and 4 carry it. Since part 19e (decision 76) it is a Notebook note: the move (table MG) and a bundle's import
 * (table MB) read it with {@link readBookNote}; nothing writes one.
 */
export interface BookNote {
  readonly id: string;
  readonly bookId: string;
  readonly kind: NoteKind;
  /** The character's or place's name, as typed; empty shows as unnamed. */
  readonly name: string;
  readonly text: string;
  /** When it was made on this device, in milliseconds since the Unix epoch: its place in the list (K4). */
  readonly createdAt: number;
}

const NOTE_KEYS = ['id', 'bookId', 'kind', 'name', 'text', 'createdAt'] as const;

/** Reads a book note record (R1–R3, part 19c, K1); anything else throws {@link StoreRecordError}. */
export function readBookNote(value: unknown): BookNote {
  const fields = readFields(value, NOTE_KEYS, 'a book note');
  const kind = NOTE_KINDS.find((known) => known === fields.get('kind'));
  if (kind === undefined) throw new StoreRecordError('kind', 'must be character or place');
  return {
    id: fields.id('id'),
    bookId: fields.id('bookId'),
    kind,
    name: fields.string('name'),
    text: fields.string('text'),
    createdAt: fields.count('createdAt', 'milliseconds'),
  };
}

/** Where a comment came from (decision 58, C6): a Word file's comment (part 23c); a beta reader's comes with part 39. */
export type CommentOrigin = 'word';

/** Every comment origin. */
export const COMMENT_ORIGINS: readonly CommentOrigin[] = Object.freeze(['word']);

/** A comment's place in its chapter's visible text (part 23c, AN1): from `from` to `to`, `from` ≤ `to`. */
export interface CommentRange {
  readonly from: number;
  readonly to: number;
}

/**
 * A comment on a chapter as database versions 7 to 9 stored it (part 23c log, table CR) and bundles of version 4 carry it:
 * plain text (decision 59) by its author, anchored by its quote and the context each side, with its range in the visible
 * text of the chapter when it was stored (null when the quote wasn't found there, AN5). Since part 19e (decision 76) it is
 * a Word note: the move (table MG) and a bundle's import (table MB) read it with {@link readCommentRecord}; nothing
 * writes one.
 */
export interface CommentRecord {
  readonly id: string;
  readonly bookId: string;
  readonly chapterId: string;
  /**
   * Part 23c, ID′ (deep-fix r1): the SHA-256, 64 lowercase hex digits, of what the source says the comment is, whatever
   * book holds it; the ID is made from it and the book's, so a book imported as a copy makes its comments' IDs again (L11).
   */
  readonly sourceKey: string;
  readonly origin: CommentOrigin;
  /** Who wrote it, as the source names them (Word's author); empty when it names none. */
  readonly author: string;
  readonly text: string;
  /** When its author wrote it, as the source says, in milliseconds since the Unix epoch; null when it says nothing usable. */
  readonly createdAt: number | null;
  /** When this device stored it, in milliseconds since the Unix epoch. */
  readonly storedAt: number;
  readonly range: CommentRange | null;
  readonly quote: string;
  /** About 30 characters of the visible text before the quote, and after it. */
  readonly before: string;
  readonly after: string;
}

const COMMENT_KEYS = ['id', 'bookId', 'chapterId', 'sourceKey', 'origin', 'author', 'text', 'createdAt', 'storedAt', 'range', 'quote', 'before', 'after'] as const;

/** Reads a comment record (R1–R3, part 23c, CR1); anything else throws {@link StoreRecordError}. */
export function readCommentRecord(value: unknown): CommentRecord {
  const fields = readFields(value, COMMENT_KEYS, 'a comment');
  const origin = COMMENT_ORIGINS.find((known) => known === fields.get('origin'));
  if (origin === undefined) throw new StoreRecordError('origin', `must be one of ${COMMENT_ORIGINS.join(', ')}`);
  const range = fields.get('range');
  return {
    id: fields.id('id'),
    bookId: fields.id('bookId'),
    chapterId: fields.id('chapterId'),
    sourceKey: fields.hash('sourceKey'),
    origin,
    author: fields.string('author'),
    text: fields.string('text'),
    createdAt: fields.get('createdAt') === null ? null : fields.count('createdAt', 'milliseconds or null'),
    storedAt: fields.count('storedAt', 'milliseconds'),
    range: range === null ? null : readRange(range),
    quote: fields.string('quote'),
    before: fields.string('before'),
    after: fields.string('after'),
  };
}

/** Reads the `range` field of a comment or an anchor, at `path` (R2): two whole numbers, the first not past the second. */
function readRange(value: unknown, path = 'range'): CommentRange {
  const fields = readFields(value, ['from', 'to'], 'a range', path);
  const from = fields.count('from', 'characters');
  const to = fields.count('to', 'characters');
  if (to < from) throw new StoreRecordError(`${path}.to`, `must not be before ${path}.from`);
  return { from, to };
}

/**
 * Who wrote a note (decision 76): the writer, a Word comment's author (decision 64), a reading link's reader (decision
 * 58, part 38d), or an invitee (decision 75, part 38e).
 */
export type NoteOrigin = 'writer' | 'word' | 'reader' | 'invitee';

/** Every note origin. */
export const NOTE_ORIGINS: readonly NoteOrigin[] = Object.freeze(['writer', 'word', 'reader', 'invitee']);

/**
 * Part 38d, DP5: the reading link's item a note came from or went out as: the link's record, the relay's thread and the
 * item, each as 32 lowercase hex digits (their 16 bytes). The writer's reply is sent on the thread; the item names it on
 * every pull.
 */
export interface NoteLink {
  readonly link: string;
  readonly thread: string;
  readonly item: string;
}

/** Part 38e: a group a role key encrypts notes for (decision 76): a role's, or everyone on the book. */
export type KeyGroup = InviteRole | 'everyone';

/** Every key group, in the order the composer offers them (everyone first). */
export const KEY_GROUPS: readonly KeyGroup[] = Object.freeze(['everyone', 'editor', 'beta-reader', 'proofreader']);

/**
 * Who may see a note (decision 76; part 38e, NR8): only the writer; a key group of the book's share; or `person`: one
 * invitee's own thread with the writer.
 */
export type NoteVisibility = 'only-me' | KeyGroup | 'person';

/** Every note visibility. */
export const NOTE_VISIBILITIES: readonly NoteVisibility[] = Object.freeze(['only-me', ...KEY_GROUPS, 'person']);

/**
 * Part 38e (NR8, tables WN and PN): a note across a share: the share's record, the note's ID at the relay (a writer's
 * note's own ID; an invitee's derived, DN8), its thread (an invite, exactly for `person`), its author (an invite; null for
 * the writer), the epoch of its key (0 under a person key), and what the relay holds of it: the SHA-256 of the item it
 * last took (64 lowercase hex digits), `deleted` once it took the delete, `refused` when it never will take it (WN8), or
 * null while it holds nothing (a writer's note not yet sent).
 */
export interface NoteShare {
  readonly share: string;
  readonly note: string;
  readonly thread: string | null;
  readonly author: string | null;
  readonly epoch: number;
  readonly sent: string | null;
  /** Deep-fix r4 (table AR′): the passage's range as last sent, so a range that only moved is told from a changed note. */
  readonly range?: CommentRange;
}

/**
 * Where a note sits in its chapter (decision 58, C7; part 19e, table AR): its range in the chapter's visible text when it
 * was stored or last acted on (null: never found there), its quote and about 30 characters of context each side.
 */
export interface NoteAnchor {
  readonly range: CommentRange | null;
  readonly quote: string;
  readonly before: string;
  readonly after: string;
}

/**
 * A note (decisions 62, 76: one record per note; part 19e log, table NR): on a chapter, anchored to a passage or on the
 * whole chapter, or in the book's Notebook (`chapterId` null), with a title and group there; by the writer or from a Word
 * file; a thread's first note or a reply to one; resolved or open; or a tombstone (`deletedAt`), which keeps no words
 * (principle 7).
 */
export interface NoteRecord {
  readonly id: string;
  readonly bookId: string;
  /** The chapter it sits on; null in the Notebook. */
  readonly chapterId: string | null;
  /** Its passage; null on the whole chapter, in the Notebook and on a reply (NR2). */
  readonly anchor: NoteAnchor | null;
  /** A Notebook note's title and group; empty otherwise (NR3). */
  readonly title: string;
  readonly group: string;
  readonly origin: NoteOrigin;
  /** The source's name for its author (Word's); empty for the writer (NR4). */
  readonly author: string;
  /** A Word comment's key (part 23c, ID″), 64 lowercase hex digits; null for the writer's (NR4). */
  readonly sourceKey: string | null;
  readonly visibility: NoteVisibility;
  /** The thread's first note; null for a first note. */
  readonly parentId: string | null;
  /** Whether its thread is resolved; never a reply's (NR5). */
  readonly resolved: boolean;
  /** Plain text (decision 59 for others' notes); the writer's notes are plain text too. */
  readonly text: string;
  /** When its author wrote it, as its source says, in milliseconds since the Unix epoch; null when unknown. */
  readonly writtenAt: number | null;
  /** When this device stored it, in milliseconds since the Unix epoch: its place among notes stored together. */
  readonly storedAt: number;
  /** When its words or anchor last changed here. */
  readonly editedAt: number;
  /** When it was deleted, leaving this tombstone; null while it stands (NR6). */
  readonly deletedAt: number | null;
  /**
   * Part 38d (NR7): the reading link's item it is: every reader's note has one, a writer's reply sent to a reader has
   * one, no other note does; a record stored before part 38d has none.
   */
  readonly link?: NoteLink;
  /** Part 38e (NR8): the share it went out on or came in by; exactly when its visibility isn't Only me. */
  readonly shared?: NoteShare;
}

const NOTE_RECORD_KEYS = ['id', 'bookId', 'chapterId', 'anchor', 'title', 'group', 'origin', 'author', 'sourceKey', 'visibility', 'parentId', 'resolved', 'text', 'writtenAt', 'storedAt', 'editedAt', 'deletedAt'] as const;

/** Reads a note's `anchor` (NR1): a range or null, its quote and context. */
function readAnchor(value: unknown): NoteAnchor {
  const fields = readFields(value, ['range', 'quote', 'before', 'after'], 'an anchor', 'anchor');
  const range = fields.get('range');
  return { range: range === null ? null : readRange(range, 'anchor.range'), quote: fields.string('quote'), before: fields.string('before'), after: fields.string('after') };
}

/** The largest epoch (part 38a's columns are Postgres `integer`). */
const MAX_NOTE_EPOCH = 2 ** 31 - 1;

/** Reads a note's `shared` (NR8). */
function readNoteShare(value: unknown): NoteShare {
  const placed = typeof value === 'object' && value !== null && Object.hasOwn(value, 'range');
  const fields = readFields(value, ['share', 'note', 'thread', 'author', 'epoch', 'sent', ...(placed ? ['range'] : [])], 'a note’s share', 'shared');
  const orNull = (key: string): string | null => (fields.get(key) === null ? null : fields.id(key));
  const epoch = fields.count('epoch', 'epochs');
  if (epoch > MAX_NOTE_EPOCH) throw new StoreRecordError('shared.epoch', 'must be below 2^31');
  const sent = fields.get('sent');
  if (sent !== null && sent !== 'deleted' && sent !== 'refused' && (typeof sent !== 'string' || !/^[0-9a-f]{64}$/u.test(sent))) {
    throw new StoreRecordError('shared.sent', 'must be a hash, deleted, refused or null');
  }
  return { share: fields.id('share'), note: fields.id('note'), thread: orNull('thread'), author: orNull('author'), epoch, sent, ...(placed ? { range: readRange(fields.get('range'), 'shared.range') } : {}) };
}

/** Reads a note's `link` (NR7): three IDs. */
function readNoteLink(value: unknown): NoteLink {
  const fields = readFields(value, ['link', 'thread', 'item'], 'a note link', 'link');
  return { link: fields.id('link'), thread: fields.id('thread'), item: fields.id('item') };
}

/** Reads a note record (R1–R3; part 19e, NR1–NR6; part 38d, NR7; part 38e, NR8); anything else throws {@link StoreRecordError}. */
export function readNote(value: unknown): NoteRecord {
  const linked = typeof value === 'object' && value !== null && Object.hasOwn(value, 'link');
  const shared = typeof value === 'object' && value !== null && Object.hasOwn(value, 'shared');
  const fields = readFields(value, [...NOTE_RECORD_KEYS, ...(linked ? ['link'] : []), ...(shared ? ['shared'] : [])], 'a note');
  const origin = NOTE_ORIGINS.find((known) => known === fields.get('origin'));
  if (origin === undefined) throw new StoreRecordError('origin', `must be one of ${NOTE_ORIGINS.join(', ')}`);
  const visibility = NOTE_VISIBILITIES.find((known) => known === fields.get('visibility'));
  if (visibility === undefined) throw new StoreRecordError('visibility', `must be one of ${NOTE_VISIBILITIES.join(', ')}`);
  const resolved = fields.get('resolved');
  if (typeof resolved !== 'boolean') throw new StoreRecordError('resolved', 'must be true or false');
  const orNull = <T>(key: string, read: (key: string) => T): T | null => (fields.get(key) === null ? null : read(key));
  const anchor = fields.get('anchor');
  const note: NoteRecord = {
    id: fields.id('id'),
    bookId: fields.id('bookId'),
    chapterId: orNull('chapterId', (key) => fields.id(key)),
    anchor: anchor === null ? null : readAnchor(anchor),
    title: fields.string('title'),
    group: fields.string('group'),
    origin,
    author: fields.string('author'),
    sourceKey: orNull('sourceKey', (key) => fields.hash(key)),
    visibility,
    parentId: orNull('parentId', (key) => fields.id(key)),
    resolved,
    text: fields.string('text'),
    writtenAt: orNull('writtenAt', (key) => fields.count(key, 'milliseconds or null')),
    storedAt: fields.count('storedAt', 'milliseconds'),
    editedAt: fields.count('editedAt', 'milliseconds'),
    deletedAt: orNull('deletedAt', (key) => fields.count(key, 'milliseconds or null')),
    ...(linked ? { link: readNoteLink(fields.get('link')) } : {}),
    ...(shared ? { shared: readNoteShare(fields.get('shared')) } : {}),
  };
  // NR2: an anchor only on a first note of a chapter.
  if (note.anchor !== null && (note.chapterId === null || note.parentId !== null)) throw new StoreRecordError('anchor', 'must be null in the Notebook and on a reply');
  // NR3: a title and group only on a Notebook note's first note.
  if ((note.title !== '' || note.group !== '') && (note.chapterId !== null || note.parentId !== null)) throw new StoreRecordError('title', 'must be empty but on a Notebook note');
  // NR4: the writer's notes name no author or key; a Word note has its key; a reader's names who, with no key.
  if (origin === 'writer' ? note.author !== '' || note.sourceKey !== null : (origin === 'word') !== (note.sourceKey !== null)) {
    throw new StoreRecordError('sourceKey', 'must be null for the writer and a reader, and a key for Word');
  }
  // NR7: a reader's note is its link's item; the writer's only as a reply; a Word note never.
  if (origin === 'reader' ? note.link === undefined : note.link !== undefined && (origin === 'word' || note.parentId === null)) {
    throw new StoreRecordError('link', 'must be on every reader’s note, and a writer’s only on a reply');
  }
  // NR7, NR8: an invitee's note is never a link's.
  if (origin === 'invitee' && note.link !== undefined) throw new StoreRecordError('link', 'must be absent on an invitee’s note');
  // NR8: a share part exactly when the note isn't Only me; an invitee's names its author, the writer's none; a person
  // thread names its invite, a group none; a reader's or Word's note never goes across a share.
  const part = note.shared;
  if ((part === undefined) !== (visibility === 'only-me')) throw new StoreRecordError('shared', 'must be present exactly when the note isn’t Only me');
  if (part !== undefined) {
    if (origin === 'word' || origin === 'reader') throw new StoreRecordError('shared', 'must be absent on a reader’s or a Word note');
    if ((origin === 'invitee') !== (part.author !== null)) throw new StoreRecordError('shared.author', 'must name the invite of an invitee’s note, and be null for the writer’s');
    if ((visibility === 'person') !== (part.thread !== null)) throw new StoreRecordError('shared.thread', 'must name an invite exactly in a person’s thread');
    if (visibility === 'person' && part.epoch !== 0) throw new StoreRecordError('shared.epoch', 'must be 0 in a person’s thread');
  } else if (origin === 'invitee') {
    throw new StoreRecordError('shared', 'must be present on an invitee’s note');
  }
  // NR5: a thread is resolved by its first note.
  if (note.resolved && note.parentId !== null) throw new StoreRecordError('resolved', 'must be false on a reply');
  // NR6: a tombstone keeps no words.
  if (note.deletedAt !== null && (note.text !== '' || note.title !== '' || note.group !== '' || note.author !== '' || note.anchor !== null)) {
    throw new StoreRecordError('deletedAt', 'a deleted note must keep no words');
  }
  return note;
}

/** Part 38d: why a reading link ended, as the relay said (part 38's table LS), or `gone`: the relay holds it no more. */
export type ShareLinkEnd = 'revoked' | 'expired' | 'disabled' | 'abandoned' | 'gone';

/** Every way a reading link ends. */
export const SHARE_LINK_ENDS: readonly ShareLinkEnd[] = Object.freeze(['revoked', 'expired', 'disabled', 'abandoned', 'gone']);

/**
 * A reading link's record (decisions 58, 62: one record per share link; part 38d log, DP1–DP3, table SR): everything that
 * holds the link's power, kept only here, in bundles and backups: its fragment key and owner secret, with the book, the
 * chapters it shares and the snapshot pinned for each, its name (only the writer sees it), whether readers may comment,
 * the end the writer set, when it was made, the sealed size sent, whether every part went up, and its end once the relay
 * said so.
 */
export interface ShareLinkRecord {
  /** The link's ID at the relay, its 16 bytes as 32 lowercase hex digits (SR1). */
  readonly id: string;
  readonly bookId: string;
  readonly name: string;
  /** The fragment key K, 32 bytes as 64 lowercase hex digits (SR2). */
  readonly key: string;
  /** The owner secret S, 16 bytes as 32 lowercase hex digits (SR2). */
  readonly secret: string;
  /** The chapters shared, in the book's order, each once (SR3). */
  readonly chapters: readonly string[];
  /** The snapshot pinned for each chapter, in the same order (SR3). */
  readonly snapshots: readonly string[];
  readonly comments: boolean;
  /** The end the writer set, in milliseconds since the Unix epoch, or null. */
  readonly expiresAt: number | null;
  readonly createdAt: number;
  /** The sealed size the relay was told, in bytes. */
  readonly size: number;
  /** Whether every part was stored (the relay made it live). */
  readonly uploaded: boolean;
  /** When this device learned the link ended, and how; null while it may be live. */
  readonly ended: { readonly at: number; readonly why: ShareLinkEnd } | null;
}

const SHARE_LINK_KEYS = ['id', 'bookId', 'name', 'key', 'secret', 'chapters', 'snapshots', 'comments', 'expiresAt', 'createdAt', 'size', 'uploaded', 'ended'] as const;

/** Reads a list of IDs at `key` (SR3): each an ID, none twice, at least one. */
function readIds(fields: Fields, key: string): string[] {
  const value = fields.get(key);
  if (!Array.isArray(value) || value.length === 0) throw new StoreRecordError(fields.name(key), 'must be a list of at least one ID');
  const ids = value.map((id: unknown, index) => {
    if (!isId(id)) throw new StoreRecordError(`${key}[${String(index)}]`, 'must be 32 lowercase hex digits');
    return id;
  });
  if (new Set(ids).size !== ids.length) throw new StoreRecordError(fields.name(key), 'must not hold an ID twice');
  return ids;
}

/** Reads a reading link's record (R1–R3; part 38d, SR1–SR4); anything else throws {@link StoreRecordError}. */
export function readShareLink(value: unknown): ShareLinkRecord {
  const fields = readFields(value, SHARE_LINK_KEYS, 'a share link');
  const key = fields.hash('key');
  const comments = fields.get('comments');
  const uploaded = fields.get('uploaded');
  if (typeof comments !== 'boolean') throw new StoreRecordError('comments', 'must be true or false');
  if (typeof uploaded !== 'boolean') throw new StoreRecordError('uploaded', 'must be true or false');
  const chapters = readIds(fields, 'chapters');
  const snapshots = readIds(fields, 'snapshots');
  if (snapshots.length !== chapters.length) throw new StoreRecordError('snapshots', 'must hold one snapshot per chapter');
  const size = fields.count('size', 'bytes');
  if (size < 1) throw new StoreRecordError('size', 'must be at least 1 byte');
  const ended = fields.get('ended');
  let end: ShareLinkRecord['ended'] = null;
  if (ended !== null) {
    const parts = readFields(ended, ['at', 'why'], 'an end', 'ended');
    const why = SHARE_LINK_ENDS.find((known) => known === parts.get('why'));
    if (why === undefined) throw new StoreRecordError('ended.why', `must be one of ${SHARE_LINK_ENDS.join(', ')}`);
    end = { at: parts.count('at', 'milliseconds'), why };
  }
  return {
    id: fields.id('id'),
    bookId: fields.id('bookId'),
    name: fields.string('name'),
    key,
    secret: fields.id('secret'),
    chapters,
    snapshots,
    comments,
    expiresAt: fields.get('expiresAt') === null ? null : fields.count('expiresAt', 'milliseconds or null'),
    createdAt: fields.count('createdAt', 'milliseconds'),
    size,
    uploaded,
    ended: end,
  };
}

/** Part 38b: an invitee's role (decision 75): it presets the person's settings and names their notes' group (decision 76). */
export type InviteRole = 'editor' | 'beta-reader' | 'proofreader';

/** Every invitee role, in the order the dialog offers them. */
export const INVITE_ROLES: readonly InviteRole[] = Object.freeze(['editor', 'beta-reader', 'proofreader']);

/** Part 38b: how far an invitee reaches into the notes (decision 75). */
export type InviteNotes = 'none' | 'read' | 'write';

/** Every notes level. */
export const INVITE_NOTES: readonly InviteNotes[] = Object.freeze(['none', 'read', 'write']);

/**
 * Part 38b (decision 75; DI1, DI3, table IR): one invite of a share, as the writer's device keeps it: the address invited,
 * the settings, its claim secret and person key (which only the invite link carries out), when it was made and last
 * changed, the settings the relay last took (`sent`, null while the relay isn't known to hold it) and when the relay took
 * its removal (DI8).
 */
export interface InviteRecord {
  /** The invite's ID at the relay, its 16 bytes as 32 lowercase hex digits (derived from the claim secret, DI2). */
  readonly id: string;
  /** The address invited, in canonical form. */
  readonly email: string;
  readonly role: InviteRole;
  readonly notes: InviteNotes;
  /** Whether the invitee's export buttons show (decision 75: the buttons only). */
  readonly export: boolean;
  /** The end the writer set, in milliseconds since the Unix epoch, or null. */
  readonly endsAt: number | null;
  /**
   * The chapters given: a range from one chapter to another, by their IDs (the live chapters between them in the book's
   * order, those put inside it later too; table CS), or null for every chapter of the book, now and later.
   */
  readonly chapters: InviteRange | null;
  /** The claim secret C, 16 bytes as 32 lowercase hex digits (DI1). */
  readonly claim: string;
  /** The person key K, 32 bytes as 64 lowercase hex digits (DI1). */
  readonly key: string;
  readonly createdAt: number;
  /** When its settings last changed here. */
  readonly changedAt: number;
  /** The settings the relay last took, as {@link InviteRecord}'s sender writes them; null while unsent (table IR, U). */
  readonly sent: string | null;
  /** When the relay took its removal (DI8); null while it may be open. */
  readonly removedAt: number | null;
}

/** Part 38b, table CS: a range of chapters, from one to another by their IDs (the same for one chapter). */
export interface InviteRange {
  readonly from: string;
  readonly to: string;
}

/** Part 38b: why a share ended, as the relay said: ended (by its record or by session), or `gone` (the relay holds it no more). */
export type ShareEnd = 'ended' | 'gone';

/** Every way a share ends. */
export const SHARE_ENDS: readonly ShareEnd[] = Object.freeze(['ended', 'gone']);

/**
 * Part 38b (table PU, DI7): an item of the copy as this device last pushed it: the item (a chapter's ID, or `m-` and an
 * invite's ID for its manifest), the version (16 bytes as 32 lowercase hex digits), the hash of its plaintext (64
 * lowercase hex digits) and the invites it was wrapped for, so an item unchanged and held by no one new isn't pushed again.
 */
export interface PushedItem {
  readonly item: string;
  readonly version: string;
  readonly hash: string;
  /** The invites it was wrapped for, by ID, each once. */
  readonly holders: readonly string[];
}

/**
 * Part 38e (DN2, table RK): one role key of a share: its group, its epoch (seconds since 2026-01-01, rising), the key
 * (32 bytes as 64 lowercase hex digits), the invites the relay took its wraps for, by ID (none yet: made, not sent), and
 * (deep-fix r2, table RJ) the invites never to be sent it: those who had left the group, and not come back, when it was
 * made.
 */
export interface RoleKey {
  readonly group: KeyGroup;
  readonly epoch: number;
  readonly key: string;
  readonly holders: readonly string[];
  readonly withheld: readonly string[];
  /** Deep-fix r4, table KP: invites its wraps were sent to whose answer never came (record first): sent again, and leavers if they go. */
  readonly pending: readonly string[];
}

/**
 * A share's record (decisions 62, 75: one record per invite set of a book; part 38b log, DI1–DI3): everything that holds
 * its power, kept only here, in bundles and backups: the owner secret, each invite's secrets and settings, whether the
 * relay took the share, what was last pushed of the copy, its role keys (part 38e) and its end once the relay said so.
 */
export interface ShareRecord {
  /** The share's ID at the relay, its 16 bytes as 32 lowercase hex digits (derived from the owner secret, DI2). */
  readonly id: string;
  readonly bookId: string;
  /** The owner secret S, 16 bytes as 32 lowercase hex digits (DI1). */
  readonly secret: string;
  readonly createdAt: number;
  /** Whether the relay took the share (`POST /v1/shares`). */
  readonly made: boolean;
  readonly invites: readonly InviteRecord[];
  readonly pushed: readonly PushedItem[];
  /** Part 38e: its role keys, by group and epoch, each once; a record stored before part 38e has none. */
  readonly roleKeys: readonly RoleKey[];
  /** When this device learned the share ended, and how; null while it may be open. */
  readonly ended: { readonly at: number; readonly why: ShareEnd } | null;
}

const SHARE_KEYS = ['id', 'bookId', 'secret', 'createdAt', 'made', 'invites', 'pushed', 'ended'] as const;
const INVITE_KEYS = ['id', 'email', 'role', 'notes', 'export', 'endsAt', 'chapters', 'claim', 'key', 'createdAt', 'changedAt', 'sent', 'removedAt'] as const;
const PUSHED_KEYS = ['item', 'version', 'hash', 'holders'] as const;
const ROLE_KEY_KEYS = ['group', 'epoch', 'key', 'holders', 'withheld', 'pending'] as const;

/** An item of a copy: a chapter's ID, or an invite's manifest (`m-` and its ID). */
const ITEM = /^(?:m-)?[0-9a-f]{32}$/u;

/** A list at `key` of `fields`, or a StoreRecordError. */
function listAt(fields: Fields, key: string): readonly unknown[] {
  const value = fields.get(key);
  if (!Array.isArray(value)) throw new StoreRecordError(fields.name(key), 'must be a list');
  return value;
}

/** Reads a range of chapters at `path` (SH2, table CS): its two ends' IDs. */
function readChapterRange(value: unknown, path: string): InviteRange {
  const fields = readFields(value, ['from', 'to'], 'a range of chapters', path);
  return { from: fields.id('from'), to: fields.id('to') };
}

/** Reads one invite of a share at `path` (SH2). */
function readInvite(value: unknown, path: string): InviteRecord {
  const fields = readFields(value, INVITE_KEYS, 'an invite', path);
  const role = INVITE_ROLES.find((known) => known === fields.get('role'));
  if (role === undefined) throw new StoreRecordError(fields.name('role'), `must be one of ${INVITE_ROLES.join(', ')}`);
  const notes = INVITE_NOTES.find((known) => known === fields.get('notes'));
  if (notes === undefined) throw new StoreRecordError(fields.name('notes'), `must be one of ${INVITE_NOTES.join(', ')}`);
  const exported = fields.get('export');
  if (typeof exported !== 'boolean') throw new StoreRecordError(fields.name('export'), 'must be true or false');
  const orNull = <T>(key: string, read: (key: string) => T): T | null => (fields.get(key) === null ? null : read(key));
  const sent = fields.get('sent');
  if (sent !== null && typeof sent !== 'string') throw new StoreRecordError(fields.name('sent'), 'must be a string or null');
  return {
    id: fields.id('id'),
    email: fields.string('email'),
    role,
    notes,
    export: exported,
    endsAt: orNull('endsAt', (key) => fields.count(key, 'milliseconds or null')),
    chapters: fields.get('chapters') === null ? null : readChapterRange(fields.get('chapters'), fields.name('chapters')),
    claim: fields.id('claim'),
    key: fields.hash('key'),
    createdAt: fields.count('createdAt', 'milliseconds'),
    changedAt: fields.count('changedAt', 'milliseconds'),
    sent,
    removedAt: orNull('removedAt', (key) => fields.count(key, 'milliseconds or null')),
  };
}

/** Reads one pushed item at `path` (SH3). */
function readPushed(value: unknown, path: string): PushedItem {
  const fields = readFields(value, PUSHED_KEYS, 'a pushed item', path);
  const item = fields.get('item');
  if (typeof item !== 'string' || !ITEM.test(item)) throw new StoreRecordError(fields.name('item'), 'must be a chapter’s ID or `m-` and an invite’s');
  return { item, version: fields.id('version'), hash: fields.hash('hash'), holders: readIds(fields, 'holders') };
}

/** The invite IDs at `key` of `fields`: each once, none is fine. */
function idList(fields: Fields, key: string): string[] {
  const ids = listAt(fields, key).map((id: unknown, index) => {
    if (!isId(id)) throw new StoreRecordError(`${fields.name(key)}[${String(index)}]`, 'must be 32 lowercase hex digits');
    return id;
  });
  if (new Set(ids).size !== ids.length) throw new StoreRecordError(fields.name(key), 'must not hold an ID twice');
  return ids;
}

/** Reads one role key at `path` (SH5): a group, an epoch below 2^31, a key, its holders and its withheld each once (none is fine). */
function readRoleKey(value: unknown, path: string): RoleKey {
  const fields = readFields(value, ROLE_KEY_KEYS, 'a role key', path);
  const group = KEY_GROUPS.find((known) => known === fields.get('group'));
  if (group === undefined) throw new StoreRecordError(fields.name('group'), `must be one of ${KEY_GROUPS.join(', ')}`);
  const epoch = fields.count('epoch', 'seconds');
  if (epoch > MAX_NOTE_EPOCH) throw new StoreRecordError(fields.name('epoch'), 'must be below 2^31');
  return { group, epoch, key: fields.hash('key'), holders: idList(fields, 'holders'), withheld: idList(fields, 'withheld'), pending: idList(fields, 'pending') };
}

/**
 * Reads a share's record (R1–R3; part 38b, SH1–SH4; part 38e, SH5): SH1 its IDs and secrets of their forms; SH2 its invites, each
 * strict, no invite ID twice; SH3 its pushed items, no item twice; SH4 its end; SH5 its role keys (absent before part
 * 38e: none), no group's epoch twice. Anything else throws
 * {@link StoreRecordError}. That the IDs are the secrets' own is checked where the derivations are (the web's `shares`).
 */
export function readShare(value: unknown): ShareRecord {
  const keyed = typeof value === 'object' && value !== null && Object.hasOwn(value, 'roleKeys');
  const fields = readFields(value, keyed ? [...SHARE_KEYS, 'roleKeys'] : SHARE_KEYS, 'a share');
  const made = fields.get('made');
  if (typeof made !== 'boolean') throw new StoreRecordError('made', 'must be true or false');
  const invites = listAt(fields, 'invites').map((invite, index) => readInvite(invite, `invites[${String(index)}]`));
  if (new Set(invites.map((invite) => invite.id)).size !== invites.length) throw new StoreRecordError('invites', 'must not hold an invite twice');
  const pushed = listAt(fields, 'pushed').map((item, index) => readPushed(item, `pushed[${String(index)}]`));
  if (new Set(pushed.map((item) => item.item)).size !== pushed.length) throw new StoreRecordError('pushed', 'must not hold an item twice');
  const roleKeys = keyed ? listAt(fields, 'roleKeys').map((key, index) => readRoleKey(key, `roleKeys[${String(index)}]`)) : [];
  if (new Set(roleKeys.map((key) => `${key.group}:${String(key.epoch)}`)).size !== roleKeys.length) throw new StoreRecordError('roleKeys', 'must not hold a group’s epoch twice');
  const ended = fields.get('ended');
  let end: ShareRecord['ended'] = null;
  if (ended !== null) {
    const parts = readFields(ended, ['at', 'why'], 'an end', 'ended');
    const why = SHARE_ENDS.find((known) => known === parts.get('why'));
    if (why === undefined) throw new StoreRecordError('ended.why', `must be one of ${SHARE_ENDS.join(', ')}`);
    end = { at: parts.count('at', 'milliseconds'), why };
  }
  return {
    id: fields.id('id'),
    bookId: fields.id('bookId'),
    secret: fields.id('secret'),
    createdAt: fields.count('createdAt', 'milliseconds'),
    made,
    invites,
    pushed,
    roleKeys,
    ended: end,
  };
}

/**
 * A bundle import under way (part 24c, principle 1): the book it creates, by the book's ID, and when it began. While one
 * is stored the book is hidden from the book list, and the next turn that holds the lock abandons it (table S, S8).
 */
export interface ImportRecord {
  /** The ID of the book being imported. */
  readonly id: string;
  /** Milliseconds since the Unix epoch. */
  readonly startedAt: number;
}

const IMPORT_KEYS = ['id', 'startedAt'] as const;

/** Reads a pending import's record (R1–R3, part 24c); anything else throws {@link StoreRecordError}. */
export function readImport(value: unknown): ImportRecord {
  const fields = readFields(value, IMPORT_KEYS, 'an import');
  return { id: fields.id('id'), startedAt: fields.count('startedAt', 'milliseconds') };
}

/** What each record store holds. Later parts add theirs, with a new {@link DB_VERSION}. */
interface Records {
  readonly chapters: ChapterText;
  readonly entries: ChapterEntry;
  readonly conflictCopies: ConflictCopy;
  readonly snapshots: Snapshot;
  readonly snapshotTexts: SnapshotText;
  readonly books: BookRecord;
  readonly device: DeviceRecord;
  /** Part 19e, MG14: what is left of an earlier version's store after the move, refused by every read. */
  readonly bookNotes: never;
  readonly comments: never;
  readonly notes: NoteRecord;
  readonly shareLinks: ShareLinkRecord;
  readonly shares: ShareRecord;
  readonly imports: ImportRecord;
}

/**
 * Part 19e, MG14: the reader of a store the move emptied (`bookNotes`, `comments`): what is still there is a record the
 * move couldn't read, so every read refuses it, and nothing can be written there.
 */
function readMoved(): never {
  throw new StoreRecordError(null, 'a record of an earlier version the notes could not be moved from');
}

/** The name of a record store. */
export type StoreName = keyof Records;

/** What the record store `S` holds. */
export type RecordOf<S extends StoreName> = Records[S];

/** Every record store's reader. */
export const RECORD_STORES: { readonly [S in StoreName]: (value: unknown) => Records[S] } = Object.freeze({
  chapters: readChapterText,
  entries: readChapterEntry,
  conflictCopies: readConflictCopy,
  snapshots: readSnapshot,
  snapshotTexts: readSnapshotText,
  books: readBook,
  device: readDevice,
  bookNotes: readMoved,
  comments: readMoved,
  notes: readNote,
  shareLinks: readShareLink,
  shares: readShare,
  imports: readImport,
});

/**
 * The fields each store is indexed on, for {@link Store.readWhere} (an IndexedDB index of the same name). Every
 * store has an entry, so a new store says it has none. Part 24c adds `bookId` to the chapters' and the snapshots' (table
 * HB: everything of a book is found by its ID).
 */
export const STORE_INDEXES = Object.freeze({
  chapters: ['bookId'],
  entries: ['bookId'],
  conflictCopies: ['bookId'],
  snapshots: ['chapterId', 'bookId'],
  snapshotTexts: [],
  books: [],
  device: [],
  bookNotes: ['bookId'],
  comments: ['bookId', 'chapterId'],
  notes: ['bookId', 'chapterId'],
  shareLinks: ['bookId'],
  shares: ['bookId'],
  imports: [],
} as const satisfies { readonly [S in StoreName]: readonly (keyof Records[S] & string)[] });

/** A field store `S` is indexed on. */
export type IndexedField<S extends StoreName> = (typeof STORE_INDEXES)[S][number];

/** Reads a raw value with the reader of `store` (R1–R3). */
export function readRecord<S extends StoreName>(store: S, raw: unknown): RecordOf<S> {
  return RECORD_STORES[store](raw);
}

/** Every record store's name, in a fixed order. */
export const STORE_NAMES: readonly StoreName[] = Object.freeze([
  'chapters',
  'entries',
  'conflictCopies',
  'snapshots',
  'snapshotTexts',
  'books',
  'device',
  'bookNotes',
  'comments',
  'notes',
  'shareLinks',
  'shares',
  'imports',
]);

/** Part 24c, table HB: the stores whose `bookId` index finds a book's records (HB2–HB7). */
export const BOOK_INDEXED_STORES: readonly StoreName[] = Object.freeze(
  STORE_NAMES.filter((name) => {
    const indexes: readonly string[] = STORE_INDEXES[name];
    return indexes.includes('bookId');
  }),
);

/**
 * The database version: raised by every part that adds a store or an index, or a field an earlier build's reader would
 * refuse (fix-next 27: a DOCX export pin's title, version 9), so an earlier build still open is told it is outdated (V4)
 * before it meets one. Opening only ever adds missing stores and indexes (table V, V2), so an upgrade never deletes a
 * record; part 19e's move to version 10 (table MG) deletes only what it has written again as a note record, in the same
 * transaction. Part 38d's version 11 adds the reading links' store and a note's `link` (NR7); part 38b's version 12 the
 * shares' store.
 */
export const DB_VERSION = 12;

/** Part 19e: the version whose upgrade moves the notes (table MG). */
export const NOTES_VERSION = 10;
